// Phase 21 · Host controls (approve/reject/end + list pending requests)
import React, { useState } from "react";
import { Check, X, StopCircle, UserPlus } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function HostControlsPanel({ room, requests = [], onRefresh }) {
    const [busy, setBusy] = useState(false);

    const approve = async (reqId) => {
        try {
            const r = await fetch(`${API}/api/community/rooms/${room.id}/speaker-requests/${reqId}/approve`, {
                method: "POST", credentials: "include",
            });
            const d = await r.json();
            if (r.ok) { toast.success("Speaker approved"); onRefresh?.(); }
            else if (d?.detail?.error === "speaker_cap_reached") {
                toast.error(`Speaker cap ${d.detail.max_speakers} reached`);
            } else toast.error("Approve failed");
        } catch {}
    };

    const reject = async (reqId) => {
        try {
            const r = await fetch(`${API}/api/community/rooms/${room.id}/speaker-requests/${reqId}/reject`, {
                method: "POST", credentials: "include",
            });
            if (r.ok) { toast.success("Rejected"); onRefresh?.(); }
        } catch {}
    };

    const endRoom = async () => {
        if (!confirm("End this room?")) return;
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/community/rooms/${room.id}/end`, {
                method: "POST", credentials: "include",
            });
            if (r.ok) { toast.success("Room ended"); onRefresh?.(); }
        } catch {}
        setBusy(false);
    };

    const pending = requests.filter((r) => r.status === "pending");

    return (
        <div className="bg-[#111111] border border-[#A3A3A3]/30 rounded-2xl p-4 space-y-3" data-testid="host-controls">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-neutral-100 flex items-center gap-2">
                    <UserPlus size={14} className="text-[#A3A3A3]" /> Host controls
                </h3>
                <button
                    type="button"
                    onClick={endRoom}
                    disabled={busy || room.status === "ended"}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#FAFAFA] hover:bg-[#FAFAFA]/80 text-white font-semibold rounded-lg text-xs disabled:opacity-40"
                    data-testid="host-end-btn"
                >
                    <StopCircle size={13} /> End
                </button>
            </div>
            <div>
                <p className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">
                    Speaker requests · {pending.length} pending
                </p>
                {!pending.length && (
                    <p className="text-xs text-neutral-500 italic">Nobody's asking to speak</p>
                )}
                <div className="space-y-1.5">
                    {pending.map((req) => (
                        <div key={req.id} className="flex items-center justify-between p-2 rounded-lg bg-[#0A0A0A] border border-[#1A1A1A]" data-testid={`req-${req.id}`}>
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center text-neutral-900 text-[10px] font-bold">
                                    {req.requester_username?.[0]?.toUpperCase() || "?"}
                                </div>
                                <span className="text-xs text-neutral-200">@{req.requester_username}</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => reject(req.id)}
                                    className="w-7 h-7 rounded-lg border border-[#1A1A1A] hover:border-[#FAFAFA]/40 hover:text-[#FAFAFA] text-neutral-400 flex items-center justify-center"
                                    data-testid={`reject-${req.id}`}
                                >
                                    <X size={12} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => approve(req.id)}
                                    className="w-7 h-7 rounded-lg bg-[#CCCCCC]/20 border border-[#CCCCCC]/40 hover:bg-[#CCCCCC]/30 text-[#CCCCCC] flex items-center justify-center"
                                    data-testid={`approve-${req.id}`}
                                >
                                    <Check size={12} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
